import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { DepositTypeSelector } from './DepositTypeSelector';
import { DepositAmountInput } from './DepositAmountInput';
import { DepositFrequencySelector } from './DepositFrequencySelector';
import { DepositDateSelector } from './DepositDateSelector';
import { DepositScheduleSelector } from './DepositScheduleSelector';
import { DepositConfirmation } from './DepositConfirmation';
import './AddGoalDepositFlow.css';

export type DepositType = 'onetime' | 'recurring';
export type DepositFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface GoalDepositData {
  type: DepositType;
  amount: number;
  frequency?: DepositFrequency;
  dayOfWeek?: number; // 0-6 для weekly
  dayOfMonth?: number; // 1-31 для monthly
  dayAndMonth?: { day: number; month: number }; // для yearly
  title?: string;
  startDate?: string; // Дата первого пополнения в формате YYYY-MM-DD
  notificationTime?: string; // Время уведомления в формате HH:MM
}

export interface DepositDateData {
  dayOfWeek?: number;
  dayOfMonth?: number;
  dayAndMonth?: { day: number; month: number };
}

interface AddGoalDepositFlowProps {
  goalId: string;
  goalName: string;
  onClose: () => void;
  onSubmit: (data: GoalDepositData) => Promise<void>;
  currency?: string;
  currencyCode?: string;
  initialData?: Partial<GoalDepositData>;
  isEditMode?: boolean;
}

type Step = 'type' | 'amount' | 'frequency' | 'date' | 'schedule' | 'confirm';

export const AddGoalDepositFlow: React.FC<AddGoalDepositFlowProps> = ({ 
  goalId,
  goalName,
  onClose, 
  onSubmit, 
  currency = 'RUB', 
  currencyCode,
  initialData,
  isEditMode = false
}) => {
  const finalCurrency = currencyCode || currency;
  // В режиме редактирования начинаем с подтверждения, иначе с выбора типа
  const [step, setStep] = useState<Step>(isEditMode ? 'confirm' : 'type');
  const [data, setData] = useState<Partial<GoalDepositData>>(initialData || {});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Блокируем скролл body когда модалка открыта
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';

    return () => {
      // Восстанавливаем скролл при закрытии
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  // Определяем порядок шагов
  const getSteps = (): Step[] => {
    const baseSteps: Step[] = ['type'];
    
    // Если выбрано регулярное пополнение
    if (data.type === 'recurring') {
      baseSteps.push('frequency');      // Шаг 2: Частота (ежедневно, еженедельно и т.д.)
      baseSteps.push('amount');          // Шаг 3: Сумма
      
      // Добавляем выбор даты в зависимости от частоты
      if (data.frequency && data.frequency !== 'daily') {
        baseSteps.push('date');          // Шаг 4: День (для weekly/monthly/yearly)
      }
      
      baseSteps.push('schedule');        // Шаг 5: Дата начала и время уведомления
    } else if (data.type === 'onetime') {
      // Для разового пополнения только сумма
      baseSteps.push('amount');          // Шаг 2: Сумма (для разового)
    }
    
    baseSteps.push('confirm');           // Последний шаг: Подтверждение
    return baseSteps;
  };

  const steps = getSteps();
  const currentStepIndex = steps.indexOf(step);
  const progress = ((currentStepIndex + 1) / steps.length) * 100;

  const updateData = (updates: Partial<GoalDepositData>) => {
    setData(prev => ({ ...prev, ...updates }));
  };

  const goToPreviousStep = () => {
    // В режиме редактирования всегда возвращаемся к подтверждению
    if (isEditMode && step !== 'confirm') {
      setStep('confirm');
      return;
    }

    // Явно определяем переходы назад в зависимости от текущего шага
    switch (step) {
      case 'type':
        onClose();
        break;
      case 'frequency':
        setStep('type');
        break;
      case 'amount':
        if (data.type === 'recurring') {
          setStep('frequency');
        } else {
          setStep('type');
        }
        break;
      case 'date':
        setStep('amount');
        break;
      case 'schedule':
        if (data.frequency === 'daily') {
          setStep('amount');
        } else {
          setStep('date');
        }
        break;
      case 'confirm':
        if (data.type === 'onetime') {
          setStep('amount');
        } else {
          setStep('schedule');
        }
        break;
      default:
        onClose();
    }
  };

  const goToStep = (targetStep: Step) => {
    setStep(targetStep);
  };

  const handleSubmit = async () => {
    if (!isDataComplete()) return;
    
    try {
      setIsSubmitting(true);
      await onSubmit(data as GoalDepositData);
    } catch (error) {
      console.error('Error submitting deposit:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDataComplete = (): boolean => {
    if (!data.type || !data.amount || data.amount <= 0) return false;
    
    if (data.type === 'recurring') {
      if (!data.frequency) return false;
      
      // Проверяем наличие дня недели для weekly
      if (data.frequency === 'weekly' && data.dayOfWeek === undefined) return false;
      
      // Проверяем наличие дня месяца для monthly
      if (data.frequency === 'monthly' && data.dayOfMonth === undefined) return false;
      
      // Проверяем наличие дня и месяца для yearly
      if (data.frequency === 'yearly' && !data.dayAndMonth) return false;
    }
    
    return true;
  };

  const renderStep = () => {
    switch (step) {
      case 'type':
        return (
          <DepositTypeSelector
            value={data.type}
            onChange={(type) => {
              const newData = { ...data, type };
              setData(newData);
              
              // Определяем следующий шаг на основе выбора
              if (type === 'recurring') {
                setStep('frequency');
              } else if (type === 'onetime') {
                setStep('amount');
              }
            }}
            onBack={goToPreviousStep}
            goalName={goalName}
          />
        );

      case 'frequency':
        return (
          <DepositFrequencySelector
            value={data.frequency}
            onChange={(frequency) => {
              const newData = { ...data, frequency };
              setData(newData);
              
              // В режиме редактирования сразу к подтверждению
              if (isEditMode) {
                setStep('confirm');
              } else {
                // После выбора частоты всегда идем на сумму
                setStep('amount');
              }
            }}
            onBack={goToPreviousStep}
          />
        );

      case 'amount':
        return (
          <DepositAmountInput
            value={data.amount || 0}
            onChange={(amount) => updateData({ amount })}
            onNext={() => {
              // В режиме редактирования сразу к подтверждению
              if (isEditMode) {
                setStep('confirm');
                return;
              }

              // Определяем следующий шаг после ввода суммы
              if (data.type === 'onetime') {
                setStep('confirm');
              } else if (data.type === 'recurring') {
                // Для ежедневных - сразу на расписание, для остальных - выбор дня
                if (data.frequency === 'daily') {
                  setStep('schedule');
                } else {
                  setStep('date');
                }
              }
            }}
            onBack={goToPreviousStep}
            currency={finalCurrency}
            depositType={data.type}
          />
        );

      case 'date':
        return (
          <DepositDateSelector
            frequency={data.frequency!}
            dayOfWeek={data.dayOfWeek}
            dayOfMonth={data.dayOfMonth}
            dayAndMonth={data.dayAndMonth}
            onChange={(dateData) => {
              setData(prev => ({ ...prev, ...dateData }));
              
              // В режиме редактирования сразу к подтверждению
              if (isEditMode) {
                setStep('confirm');
              } else {
                // После выбора дня всегда идем на расписание
                setStep('schedule');
              }
            }}
            onBack={goToPreviousStep}
          />
        );

      case 'schedule':
        return (
          <DepositScheduleSelector
            startDate={data.startDate}
            notificationTime={data.notificationTime}
            onChange={(scheduleData) => {
              setData(prev => ({ ...prev, ...scheduleData }));
              // После расписания всегда идем на подтверждение
              setStep('confirm');
            }}
            onBack={goToPreviousStep}
          />
        );

      case 'confirm':
        return (
          <DepositConfirmation
            data={data as GoalDepositData}
            goalName={goalName}
            onConfirm={handleSubmit}
            onEdit={goToStep}
            onBack={goToPreviousStep}
            isSubmitting={isSubmitting}
            currency={finalCurrency}
          />
        );

      default:
        return null;
    }
  };

  return createPortal(
    <div className="add-goal-deposit-flow">
      <div className="add-goal-deposit-flow__overlay" onClick={onClose} />
      <div className="add-goal-deposit-flow__container">
        <div className="add-goal-deposit-flow__content">
          {renderStep()}
        </div>
      </div>
    </div>,
    document.body
  );
};

